!function () {
  "use strict";

  const formSelector = "[data-newsletter-form]";
  const headingSelector = "[data-newsletter-heading]";
  const successSelector = "[data-newsletter-success]";
  const errorSelector = "[data-newsletter-error]";
  const fieldsSelector = "[data-newsletter-fields]";
  const captchaResponseSelector = 'textarea[name="h-captcha-response"]';
  const successClass = "has-success";
  const errorClass = "has-error";
  const hiddenClass = "hidden";
  const duplicateHints = [
    "bereits angemeldet",
    "already subscribed",
    "already been subscribed",
    "already registered",
    "is already subscribed",
  ];

  if (customElements.get("newsletter-component")) {
    return;
  }

  customElements.define("newsletter-component", class extends HTMLElement {
    constructor() {
      super();
      this.form = null;
      this.submitHandler = null;
      this.isSubmitting = false;
    }

    connectedCallback() {
      if (window.location.pathname === "/challenge") {
        return;
      }

      this.form = this.querySelector(formSelector);

      if (!this.form) {
        return;
      }

      this.submitHandler = this.handleSubmit.bind(this);
      this.form.addEventListener("submit", this.submitHandler);
    }

    disconnectedCallback() {
      if (this.form && this.submitHandler) {
        this.form.removeEventListener("submit", this.submitHandler);
      }
    }

    async handleSubmit(event) {
      if (this.isSubmitting) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      this.isSubmitting = true;
      this.setLoadingState(true);

      try {
        const captchaToken = await this.ensureCaptchaToken();

        if (!captchaToken) {
          this.showError();
          return;
        }

        const response = await fetch(this.form.action, {
          method: "POST",
          body: new FormData(this.form),
          credentials: "same-origin",
        });

        const responseText = await response.text();
        const responseDocument = new DOMParser().parseFromString(responseText, "text/html");
        const responseForm = responseDocument.getElementById(this.form.id);

        if (this.isDuplicateSubscriber(response, responseText)) {
          this.showSuccess(responseForm);
          return;
        }

        if (response.ok || response.url.indexOf("customer_posted=true") > -1 || this.hasSuccess(responseForm)) {
          this.showSuccess(responseForm);
          return;
        }

        this.showError(responseForm);
      } catch (error) {
        this.showError();
      } finally {
        this.isSubmitting = false;
        this.setLoadingState(false);
      }
    }

    isDuplicateSubscriber(response, responseText) {
      if (response.status !== 400 || !responseText) {
        return false;
      }

      const normalizedText = responseText.toLowerCase();
      return duplicateHints.some((hint) => normalizedText.indexOf(hint) > -1);
    }

    async ensureCaptchaToken() {
      const responseInput = this.getCaptchaResponseInput();

      if (responseInput && responseInput.value) {
        return responseInput.value;
      }

      if (!window.hcaptcha || typeof window.hcaptcha.execute !== "function") {
        return null;
      }

      const widgetId = this.getCaptchaWidgetId();

      try {
        if (widgetId) {
          await window.hcaptcha.execute(widgetId, { async: true });
        } else {
          await window.hcaptcha.execute(undefined, { async: true });
        }
      } catch (error) {
        return null;
      }

      return this.waitForCaptchaResponse(responseInput);
    }

    getCaptchaResponseInput() {
      return this.form.querySelector(captchaResponseSelector) || this.form.querySelector("textarea[id^='h-captcha-response']");
    }

    getCaptchaWidgetId() {
      const captchaFrame = this.form.querySelector(".h-captcha iframe[data-hcaptcha-widget-id]");

      return captchaFrame ? captchaFrame.dataset.hcaptchaWidgetId : null;
    }

    waitForCaptchaResponse(responseInput) {
      if (!responseInput) {
        return null;
      }

      return new Promise((resolve) => {
        const startedAt = Date.now();
        const timeout = 5000;

        const checkResponse = () => {
          if (responseInput.value) {
            resolve(responseInput.value);
            return;
          }

          if (Date.now() - startedAt >= timeout) {
            resolve(null);
            return;
          }

          window.requestAnimationFrame(checkResponse);
        };

        checkResponse();
      });
    }

    hasSuccess(responseForm) {
      return !!(responseForm && (responseForm.classList.contains(successClass) || responseForm.querySelector(successSelector)));
    }

    setLoadingState(isLoading) {
      const submitButton = this.form.querySelector('[type="submit"]');

      if (!submitButton) {
        return;
      }

      submitButton.disabled = isLoading;
      submitButton.setAttribute("aria-busy", isLoading ? "true" : "false");
    }

    showSuccess(responseForm) {
      const successMessage = this.getSuccessMessage();
      const successSource = responseForm ? responseForm.querySelector(successSelector) : null;

      if (successMessage) {
        if (successSource) {
          successMessage.innerHTML = successSource.innerHTML;
        }

        successMessage.classList.remove(hiddenClass);
      }

      this.toggleState(successClass, errorClass);
      this.toggleFields(true);
      this.hideHeading();
      this.focusMessage(successMessage);
    }

    showError(responseForm) {
      const errorMessage = this.getErrorMessage();
      const errorSource = responseForm ? responseForm.querySelector(errorSelector) : null;

      if (errorMessage) {
        if (errorSource) {
          errorMessage.innerHTML = errorSource.innerHTML;
        }

        errorMessage.classList.remove(hiddenClass);
      }

      this.toggleState(errorClass, successClass);
      this.toggleFields(false);
      this.hideHeading();
      this.focusMessage(errorMessage);
    }

    toggleState(activeClass, inactiveClass) {
      this.form.classList.remove(hiddenClass);
      this.form.classList.remove(inactiveClass);
      this.form.classList.add(activeClass);
    }

    toggleFields(shouldHide) {
      const fields = this.form.querySelector(fieldsSelector);

      if (!fields) {
        return;
      }

      fields.classList.toggle(hiddenClass, shouldHide);
    }

    hideHeading() {
      const heading = this.form.parentElement ? this.form.parentElement.querySelector(headingSelector) : null;

      if (heading) {
        heading.classList.add(hiddenClass);
      }
    }

    getSuccessMessage() {
      return this.form.querySelector(successSelector);
    }

    getErrorMessage() {
      return this.form.querySelector(errorSelector);
    }

    focusMessage(message) {
      if (message && typeof message.focus === "function") {
        message.focus({ preventScroll: true });
      }
    }
  });
}();